import React, { useId } from "react";

/* ============================================================
 * PaintedDrip — a confident hand-painted cream brushstroke that
 * extends vertically. Smooth scalloped top, gentle wavy sides,
 * scalloped bottom. NO jittery noise.
 * ============================================================ */
export const PaintedDrip = ({
  className = "",
  cream = "#FDFBF7",
  line = "#E8B4C8",
}) => (
  <svg viewBox="0 0 300 460" className={className} preserveAspectRatio="xMidYMid meet" aria-hidden>
    <defs>
      <filter id="drip-soft" x="-2%" y="-2%" width="104%" height="104%">
        <feGaussianBlur stdDeviation="0.4" />
      </filter>
    </defs>
    {/* Soft drop-shadow behind */}
    <path
      d="M150 18
         C 92 22 64 60 60 110
         C 56 168 70 224 60 282
         C 52 332 72 380 110 412
         C 138 436 162 436 190 412
         C 228 380 248 332 240 282
         C 230 224 244 168 240 110
         C 236 60 208 22 150 18 Z"
      fill="#000"
      opacity="0.18"
      transform="translate(2 8)"
      filter="url(#drip-soft)"
    />
    {/* Outer painted body with scalloped top + bottom + wavy sides */}
    <path
      d="M150 12
         C 130 12 116 18 102 18
         C 92 18 84 12 76 16
         C 62 22 56 36 54 54
         C 52 78 70 100 60 124
         C 54 144 62 168 56 192
         C 50 220 66 248 58 276
         C 50 308 70 336 60 366
         C 56 390 76 416 108 430
         C 130 440 168 440 192 430
         C 224 416 244 390 240 366
         C 232 336 250 308 242 276
         C 234 248 250 220 244 192
         C 238 168 246 144 240 124
         C 230 100 248 78 246 54
         C 244 36 238 22 224 16
         C 216 12 208 18 198 18
         C 184 18 170 12 150 12 Z"
      fill={cream}
      filter="url(#drip-soft)"
    />
    {/* Inner painted line — single stroke that traces shape, slightly inset */}
    <path
      d="M150 26
         C 132 26 118 32 106 32
         C 94 32 84 30 78 36
         C 72 46 70 60 72 76
         C 74 96 88 116 80 138
         C 74 158 82 180 76 200
         C 70 224 86 246 78 268
         C 70 296 90 322 80 350
         C 78 372 94 396 122 408
         C 144 418 168 418 188 408
         C 216 396 232 372 230 350
         C 220 322 240 296 232 268
         C 224 246 240 224 234 200
         C 228 180 236 158 230 138
         C 222 116 236 96 238 76
         C 240 60 238 46 232 36
         C 226 30 216 32 204 32
         C 192 32 178 26 150 26 Z"
      fill="none"
      stroke={line}
      strokeOpacity="0.85"
      strokeWidth="1.6"
    />
  </svg>
);

/* ============================================================
 * BlobImage — image clipped by an irregular blob path. Uses a
 * unique clipPath id per instance to avoid collisions, and renders
 * via <image> inside SVG so the photo fills the blob perfectly.
 * ============================================================ */
const BLOBS = [
  // each: 32x32 viewBox-space normalized path
  "M16 1 C 24 2 30 8 31 16 C 32 24 25 31 17 30 C 9 31 2 24 1 16 C 0 8 8 0 16 1 Z",
  "M16 1 C 25 3 31 9 30 17 C 31 24 22 31 14 29 C 6 27 1 22 1 15 C 1 7 8 1 16 1 Z",
  "M14 2 C 22 1 30 7 30 14 C 31 21 29 30 20 30 C 11 31 2 26 2 18 C 2 10 6 2 14 2 Z",
  "M16 1 C 24 1 31 8 30 17 C 30 23 24 31 16 30 C 9 31 2 25 2 17 C 2 9 8 1 16 1 Z",
];

export const BlobImage = ({
  src,
  alt = "",
  variant = 0,
  className = "",
  style,
  rotate = 0,
}) => {
  const id = useId().replace(/:/g, "");
  const d = BLOBS[variant % BLOBS.length];
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      preserveAspectRatio="xMidYMid slice"
      style={{ ...(style || {}), transform: `rotate(${rotate}deg)` }}
      aria-label={alt}
    >
      <defs>
        <clipPath id={`bp-${id}`}>
          <path d={d} />
        </clipPath>
      </defs>
      <image
        href={src}
        x="0"
        y="0"
        width="32"
        height="32"
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#bp-${id})`}
      />
    </svg>
  );
};

/* Filled blob — used as a colour splash behind a photo. */
export const BlobShape = ({
  color = "#F4D6DC",
  variant = 0,
  className = "",
  rotate = 0,
}) => (
  <svg
    viewBox="0 0 32 32"
    className={className}
    preserveAspectRatio="xMidYMid slice"
    style={{ transform: `rotate(${rotate}deg)` }}
    aria-hidden
  >
    <path d={BLOBS[variant % BLOBS.length]} fill={color} />
  </svg>
);

/* 4-pointed sparkle. */
export const Sparkle = ({ color = "#B91C52", className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path
      d="M12 2 L13.2 9.8 L21 11 L13.2 12.2 L12 20 L10.8 12.2 L3 11 L10.8 9.8 Z"
      fill={color}
    />
  </svg>
);

/* Big chunky pill button with secondary ring for retro vibes. */
export const ChunkyButton = ({
  children,
  color = "bg-magenta",
  text = "text-cream",
  ring = "ring-magenta-deep",
  className = "",
  ...props
}) => (
  <button
    {...props}
    className={`${color} ${text} rounded-full px-7 py-4 uppercase text-xs tracking-[0.24em] font-medium ring-2 ring-offset-4 ring-offset-cream ${ring} hover:translate-y-[-2px] transition-transform ${className}`}
  >
    {children}
  </button>
);

/* Retro warped checker band SVG. */
export const CheckerBand = ({
  rows = 2,
  cols = 16,
  a = "#FDFBF7",
  b = "#FF6B35",
  className = "",
  skew = -3,
}) => {
  const tiles = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      tiles.push({ r, c, color: (r + c) % 2 === 0 ? a : b });
    }
  }
  return (
    <svg
      viewBox={`0 ${-4} ${cols * 10} ${rows * 10 + 8}`}
      preserveAspectRatio="none"
      className={className}
      aria-hidden
    >
      <g transform={`skewY(${skew})`}>
        {tiles.map((t) => (
          <rect
            key={`${t.r}-${t.c}`}
            x={t.c * 10}
            y={t.r * 10}
            width="10.1"
            height="10.1"
            fill={t.color}
          />
        ))}
      </g>
    </svg>
  );
};

/* Stamp frame — double thin border for "Partner in Crime" style cards. */
export const StampFrame = ({
  outer = "#E2799C",
  inner = "#9CC9E8",
  children,
  className = "",
  testid,
}) => (
  <div data-testid={testid} className={`relative ${className}`}>
    <div className="absolute inset-2 rounded-[1.6rem] pointer-events-none" style={{ border: `2px solid ${outer}` }} />
    <div className="absolute inset-[14px] rounded-[1.3rem] pointer-events-none" style={{ border: `1.5px solid ${inner}` }} />
    <div className="relative">{children}</div>
  </div>
);

/* Script "sticker" — handwritten text, tilted, with squiggly underline. */
export const StickerNote = ({
  children,
  color = "#B91C52",
  rotate = -6,
  className = "",
  size = "text-3xl md:text-4xl",
}) => (
  <span
    className={`inline-block font-script leading-none ${size} ${className}`}
    style={{ color, transform: `rotate(${rotate}deg)` }}
  >
    {children}
  </span>
);

/* Decorative fan/dash divider used in stamp cards. */
export const FanDash = ({ color = "#B91C52", flip = false }) => (
  <svg
    viewBox="0 0 56 18"
    className="w-12 h-4"
    style={flip ? { transform: "scaleX(-1)" } : undefined}
    aria-hidden
  >
    <g stroke={color} strokeWidth="1.6" strokeLinecap="round" fill="none">
      <path d="M2 9 L36 9" />
      <path d="M40 4 L48 9 L40 14" />
      <path d="M44 4 L52 9 L44 14" />
    </g>
  </svg>
);

/* Tiny heart divider. */
export const HeartGlyph = ({ color = "#B91C52", className = "w-3 h-3" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path
      d="M12 21s-7-4.5-9.5-9C.5 8 3 4 7 4c2 0 3.5 1 5 3 1.5-2 3-3 5-3 4 0 6.5 4 4.5 8C19 16.5 12 21 12 21z"
      fill={color}
    />
  </svg>
);

/* Floating tiny stars used to decorate edges of sections. */
export const StarScatter = ({ color = "#FF6B35", className = "" }) => (
  <svg viewBox="0 0 100 100" className={className} aria-hidden>
    <g fill={color}>
      <path d="M10 22 L11.6 27 L17 28 L11.6 29 L10 34 L8.4 29 L3 28 L8.4 27 Z" />
      <path d="M30 70 L31.3 74 L35 75 L31.3 76 L30 80 L28.7 76 L25 75 L28.7 74 Z" opacity=".8" />
      <path d="M80 40 L81.3 44 L85 45 L81.3 46 L80 50 L78.7 46 L75 45 L78.7 44 Z" opacity=".7" />
      <circle cx="60" cy="20" r="1.8" />
      <circle cx="22" cy="50" r="1.4" opacity=".7" />
    </g>
  </svg>
);

/* ============================================================
 * ScallopedDisc — a giant flower-petal silhouette filled with a
 * solid colour, cropped to exactly the scalloped contour you'd
 * expect behind the Bloom Foundry hero logo.
 * ============================================================ */
// Shared scallop path generator — viewBox 0..32 coords (used by all
// scalloped components so they share the exact same silhouette).
const scallopPath = (scallops = 24) => {
  const cx = 16, cy = 16, R = 14.5;
  const step = (Math.PI * 2) / scallops;
  const chord = 2 * R * Math.sin(step / 2);
  const arcR = chord * 0.72;
  let d = "";
  for (let i = 0; i < scallops; i++) {
    const a1 = i * step - Math.PI / 2;
    const a2 = (i + 1) * step - Math.PI / 2;
    const x1 = cx + Math.cos(a1) * R;
    const y1 = cy + Math.sin(a1) * R;
    const x2 = cx + Math.cos(a2) * R;
    const y2 = cy + Math.sin(a2) * R;
    if (i === 0) d += `M${x1.toFixed(3)},${y1.toFixed(3)} `;
    d += `A${arcR.toFixed(3)},${arcR.toFixed(3)} 0 0 1 ${x2.toFixed(3)},${y2.toFixed(3)} `;
  }
  return d + "Z";
};

export const ScallopedDisc = ({
  scallops = 28,
  color = "#EC2675",
  className = "",
  innerRingColor,
}) => {
  const d = scallopPath(scallops);
  return (
    <svg viewBox="0 0 32 32" className={className} preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d={d} fill={color} />
      {innerRingColor && (
        <circle cx="16" cy="16" r="13" fill="none" stroke={innerRingColor} strokeWidth="0.15" opacity="0.7" />
      )}
    </svg>
  );
};

/* Photo clipped into the scalloped silhouette. */
export const ScallopedImage = ({
  src,
  alt = "",
  scallops = 24,
  className = "",
  style,
}) => {
  const id = useId().replace(/:/g, "");
  const d = scallopPath(scallops);
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      preserveAspectRatio="xMidYMid slice"
      style={style}
      aria-label={alt}
    >
      <defs>
        <clipPath id={`sc-${id}`}>
          <path d={d} />
        </clipPath>
      </defs>
      <image
        href={src}
        x="0"
        y="0"
        width="32"
        height="32"
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#sc-${id})`}
      />
    </svg>
  );
};

/* Scalloped outline ring — used as an outer frame around badges. */
export const ScallopedRing = ({
  scallops = 24,
  color = "#EC2675",
  strokeWidth = 0.4,
  className = "",
}) => {
  const d = scallopPath(scallops);
  return (
    <svg viewBox="0 0 32 32" className={className} preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} />
    </svg>
  );
};

/* Scalloped badge container — child content sits centred inside. */
export const ScallopedBadge = ({
  children,
  fill = "#FDFBF7",
  ring,
  scallops = 24,
  className = "",
  testid,
}) => (
  <div data-testid={testid} className={`relative ${className}`}>
    <ScallopedDisc color={fill} scallops={scallops} className="absolute inset-0 w-full h-full" />
    {ring && <ScallopedRing color={ring} scallops={scallops} className="absolute inset-[6%] w-[88%] h-[88%]" strokeWidth={0.25} />}
    <div className="relative w-full h-full flex items-center justify-center">{children}</div>
  </div>
);

/* Scalloped horizontal divider — wavy line built from arc bumps. */
export const ScallopedDivider = ({ color = "#EC2675", className = "", segments = 30 }) => {
  // Build a wavy line: alternating up/down arcs
  const segW = 100 / segments;
  let d = `M0,5 `;
  for (let i = 0; i < segments; i++) {
    const x1 = i * segW;
    const x2 = (i + 1) * segW;
    d += `A${segW / 2},${segW / 2} 0 0 1 ${x2.toFixed(2)},5 `;
  }
  return (
    <svg viewBox="0 0 100 10" preserveAspectRatio="none" className={className} aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
};
